import React, {useCallback, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {useForm} from "react-hook-form";
import {Col, Container, Form, Row} from "react-bootstrap";
import {
    makeFormDataUpdateRequest,
    makeUpdateRequest,
} from "Services/ServicesUtils";
import {API_AUDITS} from "Models/Audit/AuditEndpoints";
import {insertNotification} from "Utils/NotificationsUtils";
import {compressImage, compressVideo} from "Utils/helpers";
import {
    GSPSLabelledSelectFieldWithIcon,
} from "Components/Common/GSPSLabelledSelectFieldWithIcon";
import {GSPSLabelledInput} from "Components/Common/GSPSLabelledInput";
import {GSPSTextArea} from "Components/Common/GSPSTextAreaInput";
import {GSPSSubmitButton} from "Components/Common/GSPSSubmitButton";
import {
    GSPSPageWrapper,
    GSPSPopUpFooter,
    GSPSPopUpHeader,
} from "../Common/GSPSStyledContainers.styles";
import {GSPSCancelButton} from "../Common/GSPSCancelButton";
import {FFmpeg} from "@ffmpeg/ffmpeg";
import {AnnotationType, AnnotationType2} from "Models/Audit/AuditConstants";
import {
    getPhaseName,
    PHASE_LABEL,
    SEVERITY_LABEL,
} from 'Components/Map/SVPMapFilter';
import {MediaViewer} from "Pages/Audits/MediaViewer";
import {SVPModalDeleteButton} from "Components/Buttons/SVPModalDeleteButton";
import ConfirmationModal from "Components/Common/ConfirmationModal";
import {auditsFilterMap, baseColorOptions} from "./SVPMapConstants";
import {capitalize} from "lodash";

const ffmpeg = new FFmpeg();

export function getSelectablePhases() {
    return ([
        {id: 0, name: "N/A"}, ...Array.from({length: 10}, (_, i) => ({
            "id": Number(i + 1),
            "name": `Phase ${i + 1}`,
        }))]);
}

const GSPSAnnotationModal = ({
    isModalOpen,
    onModalClose,
    audit,
    clickedItem,
    annotationType,
    issuesSeverities,
    deleteMedia,
    deleteAuditAnnotation,
    updateItem,
    typeItems,
    replaceTypeItemFilter,
    isPublicAudit = false,
    isViewOnly = false,
}) => {
    const videoCompressionFeature = false;
    const {handleSubmit, register, setValue, reset, formState: {isDirty, touched, errors}} = useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingVideo, setLoadingVideo] = useState(false);
    const [isVideoCompressed, setIsVideoCompressed] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [availableTypeItems, setAvailableTypeItems] = useState([]);
    const [currentItem, setCurrentItem] = useState(clickedItem);
    const [deletedMedia, setDeletedMedia] = useState([]);
    const [selectedAuditTypeItem, setSelectedAuditTypeItem] = useState(null);
    const [customColorOptions, setCustomColorOptions] = useState(baseColorOptions);
    const [canDeleteModalShow, setCanDeleteModalShow] = useState(false);

    useEffect(() => {
        if (!clickedItem) return;
        if (!selectedAuditTypeItem) return;

        const newColorCode = selectedAuditTypeItem.color;

        let finalOptions = [...baseColorOptions];

        if (newColorCode && !finalOptions.some((option) => option.id === newColorCode)) {
            finalOptions.push({
                id: newColorCode,
                color_code: newColorCode,
            });
        }

        finalOptions = Array.from(new Map(finalOptions.map((item) => [item.id, item])).values());
        setCustomColorOptions(finalOptions);

        const currentColor = selectedAuditTypeItem.id === clickedItem?.audit_type_item?.id ?
            clickedItem?.custom_color || clickedItem?.audit_type_item?.color :
            selectedAuditTypeItem.color;

        setValue("custom_color", currentColor || "");
    }, [selectedAuditTypeItem, setValue, clickedItem]);

    const filterItems = useCallback((annotationType, auditTypeId) => {
        const filtered = typeItems.filter((item) =>
            item.annotation_type === annotationType && item.audit_type.id === auditTypeId).map((item) => ({
            id: item.id,
            name: item.name,
            color_code: item.color,
        }));
        setAvailableTypeItems(filtered);
    }, [typeItems]);

    useEffect(() => {
        if (!clickedItem) return;

        setCurrentItem(clickedItem);
        if (!isViewOnly) {
            filterItems(annotationType, clickedItem?.audit_type_item?.audit_type?.id);
        }

        if (clickedItem?.audit_type_item) {
            setSelectedAuditTypeItem({
                id: clickedItem.audit_type_item.id,
                name: clickedItem.audit_type_item.name,
                color: clickedItem.audit_type_item.color,
            });
        }
    }, [clickedItem, annotationType, isViewOnly, filterItems]);

    useEffect(() => {
        if (!clickedItem) return;

        if (!isViewOnly) {
            if (annotationType === AnnotationType.ISSUE) {
                setValue("issue_severity", clickedItem.severity);
                setValue("item_phase", clickedItem.item_phase);
            }
            setValue("audit_type_item_id", clickedItem.audit_type_item.id);
            setValue("description", clickedItem.description);
            setValue("notes", clickedItem.notes);
        } else {
            setAvailableTypeItems([{
                id: clickedItem.audit_type_item.id,
                name: clickedItem.audit_type_item.name,
                color_code: clickedItem.audit_type_item.color,
            }]);
            setValue("audit_type_item_id", clickedItem.audit_type_item.id);
            setValue("item_phase", clickedItem.item_phase);
        }
    }, [clickedItem, annotationType, isViewOnly, setValue]);

    const updateElementMedia = (index, mediaValue, elementType, mediaType, newNotes) => {
        const notes = newNotes;
        setCurrentItem((prev) => {
            const newData = {...prev};
            const list = newData[annotationType + "_" + mediaType] || [];

            if (index >= list.length) {
                const newMedia = {};
                if (mediaType === "videos") {
                    newMedia.video_value = mediaValue[0];
                    newMedia.notes = notes;
                } else {
                    newMedia.mediaValue = mediaValue[0];
                    newMedia.notes = notes;
                }
                newData[annotationType + "_" + mediaType] = [...list, newMedia];
            } else if (index < list.length) {
                newData[annotationType + "_" + mediaType] = list.map((img, i) => {
                    if (i === index) {
                        if (mediaType === "videos") {
                            return {...img, video_value: mediaValue[0], notes: notes};
                        }
                        return {...img, mediaValue: mediaValue[0], notes: notes};
                    }
                    return img;
                });
            }
            return newData;
        });
    };

    const updateElementImages = (rowId, newRowImages, elementType, newNotes = "") => {
        updateElementMedia(rowId, newRowImages, elementType, "images", newNotes);
    };

    const updateElementVideos = (rowId, newRowImages, elementType, newNotes = "") => {
        updateElementMedia(rowId, newRowImages, elementType, "videos", newNotes);
    };

    const handleDelete = (media, index, mediaType) => {
        setDeletedMedia((prev) => [...prev, media]);
        setCurrentItem((prev) => {
            const newData = {...prev};

            const list = newData[annotationType + "_" + mediaType + "s"] || [];
            if (index < list.length) {
                newData[annotationType + "_" + mediaType + "s"] = list.filter((_, i) => i !== index);
            }
            return newData;
        });
    };

    const deleteAuditCall = async () => {
        deleteAuditAnnotation();
        handleClose();
    };

    const handleAuditTypeItemChange = (e) => {
        const selectedId = e.target.value;
        const found = typeItems.find((item) => item.id === parseInt(selectedId) || item.id === selectedId);
        if (found) {
            setSelectedAuditTypeItem({
                id: found.id,
                name: found.name,
                color: found.color,
            });

            setValue("custom_color", found.color || "");
        }
    };

    const registerAuditTypeItem = () => {
        const baseRegister = register("audit_type_item_id");
        return {
            ...baseRegister,
            onChange: (e) => {
                baseRegister.onChange(e);
                handleAuditTypeItemChange(e);
            },
        };
    };

    const onSubmit = async (values) => {
        try {
            setIsSubmitting(true);
            await submitForm(values);
            onModalClose();
        } catch (e) {
            insertNotification("Error", `Can't add audit ${e}. Reason: create the audit first`, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setDeletedMedia([]);
        setCurrentItem(clickedItem);
        onModalClose();
        reset();
    };

    const submitForm = async (values) => {
        const addedAuditTypeItem = typeItems.find((item) => item.id === values.audit_type_item_id);
        if (addedAuditTypeItem && addedAuditTypeItem.name !== clickedItem.audit_type_item.name) {
            replaceTypeItemFilter(clickedItem.audit_type_item.name,
                addedAuditTypeItem.name,
                auditsFilterMap[annotationType]);
        }

        await deleteMedia(deletedMedia);
        setDeletedMedia([]);

        await submitImages(clickedItem.id);
        await submitVideos(clickedItem.id);

        const body = {
            ...clickedItem,
            notes: values.notes,
            location: audit.location.id,
            audit_type_item_id: clickedItem.audit_type_item.id,
            description: values.description,
        };

        body.severity = values.issue_severity ?? clickedItem.severity;

        if (values.item_phase && Number(values.item_phase) !== 0) {
            body.item_phase = values.item_phase;
        } else {
            body.item_phase = null;
        }

        if (values.custom_color) {
            body.custom_color = values.custom_color;
        } else {
            body.custom_color = clickedItem.audit_type_item?.color;
        }

        if (values.length_feet) {
            body.length_feet = values.length_feet;
        } else if (values.area_feet) {
            body.area_feet = values.area_feet;
        }

        if (annotationType === AnnotationType.ISSUE) {
            const newSeverity = issuesSeverities.find((item) => item.id === values.issue_severity);
            if (newSeverity?.name !== clickedItem.severity_object?.name) {
                replaceTypeItemFilter(clickedItem.severity_object?.name, newSeverity?.name, SEVERITY_LABEL);
            }

            const newPhase = getSelectablePhases().find((item) => item.id === values.item_phase);
            if (newPhase?.id !== clickedItem.item_phase) {
                replaceTypeItemFilter(
                    getPhaseName(clickedItem.item_phase),
                    getPhaseName(values.item_phase),
                    PHASE_LABEL,
                );
            }
        }

        body.audit_type_item_id = values.audit_type_item_id ?? clickedItem.audit_type_item.id;
        const endpoint = {area: "auditArea", issue: "auditIssue", line: "auditLine"};

        await makeUpdateRequest(`${API_AUDITS[endpoint[annotationType]]}${clickedItem.id}/`, body)
            .then(async (jsonRes) => {
                insertNotification("Success", `Audit ${annotationType} has been updated`, "success");
                updateItem(clickedItem.id, jsonRes, annotationType);
            }).catch((error) => {
                insertNotification("Error", `Can't update audit ${annotationType}. Reason: ${error.message}`, "error");
            });
    };

    const submitImages = async (auditElementId) => {
        const formData = new FormData();

        const images = currentItem[`${annotationType}_images`] || [];
        const highestSeq = images.reduce((max, img) => Math.max(max, img.image_sequence || 0), 0);

        for (let i = 0; i < images.length; i++) {
            const image = images[i];
            if (image.mediaValue) {
                const compressedImage = await compressImage(image.mediaValue);

                const sequenceNumber = image.image_sequence ?? (highestSeq + i + 1);

                formData.append("image", compressedImage);
                formData.append("image_sequence", sequenceNumber);
                formData.append("image_value", compressedImage);
                formData.append("element_type", "compressedImage");
                formData.append("notes", image.notes);
            }
        }

        await makeFormDataUpdateRequest(
            API_AUDITS.auditElementImage(annotationType + "s", auditElementId),
            formData,
        ).then(async () => { }).catch((error) => {
            insertNotification(
                "Error", `Can't update Images ${annotationType}. Reason: ${error.message}`, "error",
            );
        });
    };

    const submitVideos = async (auditElementId) => {
        if (currentItem[`${annotationType}_videos`].length < 1) {
            return;
        }

        const formData = new FormData();
        await ffmpeg.load();

        for (let i = 0; i < currentItem[`${annotationType}_videos`].length; i++) {
            const video = currentItem[`${annotationType}_videos`][i];
            let videoValue = video.video_value;
            if (!videoValue || typeof videoValue === "string" || !(videoValue instanceof File)) {
                continue;
            }

            if (videoCompressionFeature) {
                setLoadingProgress(0);
                setLoadingVideo(true);
                videoValue = await compressVideo(videoValue, ffmpeg, setLoadingProgress);
                setIsVideoCompressed(true);
            }
            formData.append("video_sequence", i);
            formData.append("video_value", videoValue);
            formData.append("element_type", "video");
            formData.append("notes", video.notes);
        }

        await makeFormDataUpdateRequest(API_AUDITS.auditElementVideo(annotationType + "s", auditElementId), formData)
            .then((jsonRes) => jsonRes)
            .catch((error) => {
                error.then((message) => {
                    insertNotification("Error", message.detail, "error");
                });
            });
        setLoadingVideo(false);
        setIsVideoCompressed(false);
    };

    function renderImagesAndVideos() {
        return <MediaViewer
            isViewOnly={isViewOnly}
            handleDelete={handleDelete}
            item={currentItem}
            annotationType={annotationType}
            updateElementImages={updateElementImages}
            updateElementVideos={updateElementVideos}
            loadingVideo={isLoadingVideo}
            loadingProgress={loadingProgress}
            isVideoCompressed={isVideoCompressed}
            videoCompressionFeature={videoCompressionFeature}
        />;
    }

    function renderDimensionInfo(inputID, inputVal, inputUnit) {
        return <>
            <GSPSLabelledInput
                controlType={"input"}
                id={inputID}
                labelName={`Dimensions (${inputUnit})`}
                errors={errors}
                register={register}
                isDisabled={isViewOnly}
                value={Number(inputVal).toFixed(2)}
            />
        </>;
    }

    if (!isModalOpen && clickedItem?.id === currentItem?.id) return null;


    return (
        <GSPSPageWrapper
            className="show"
            role="dialog"
            centered={true}
            backdrop="static"
            onHide={onModalClose}
            show={isModalOpen}
            size={"lg"}
            container={document.querySelector("#root .App")}
        >
            <GSPSPopUpHeader>
                <h5 className="subtitle-md">Edit {capitalize(AnnotationType2[annotationType])}</h5>
            </GSPSPopUpHeader>
            <Form id="issueForm" onSubmit={handleSubmit(onSubmit)}>
                <Container>
                    <Row>
                        {issuesSeverities && (
                            <Col xs={10} md={3}>
                                <GSPSLabelledSelectFieldWithIcon
                                    labelName={"Severity"}
                                    inputID={"issue_severity"}
                                    selectableOptions={issuesSeverities}
                                    register={register}
                                    isDisabled={isViewOnly}
                                    defaultValue={String(clickedItem?.severity)}
                                />
                            </Col>
                        )}
                        <Col xs={10} md={2}>
                            <GSPSLabelledSelectFieldWithIcon
                                labelName={"Phase"}
                                inputID={"item_phase"}
                                selectableOptions={getSelectablePhases()}
                                defaultValue={clickedItem?.item_phase || 0}
                                register={register}
                                isDisabled={isViewOnly}
                                isRequired={false}
                            />
                        </Col>
                        <Col xs={10} md={5}>
                            {(availableTypeItems.length >= 1) &&
                                <GSPSLabelledSelectFieldWithIcon
                                    labelName={"Inspection Item"}
                                    inputID={"audit_type_item_id"}
                                    selectableOptions={availableTypeItems}
                                    register={registerAuditTypeItem}
                                    isDisabled={isViewOnly}
                                    defaultValue={String(clickedItem.audit_type_item?.id)}
                                />
                            }
                        </Col>
                        <Col xs={10} md={2}>
                            {(availableTypeItems.length >= 1) && selectedAuditTypeItem && (
                                <GSPSLabelledSelectFieldWithIcon
                                    labelName={"Custom Color"}
                                    key={`color-${selectedAuditTypeItem.id}-${selectedAuditTypeItem.color}`}
                                    inputID={"custom_color"}
                                    selectableOptions={customColorOptions}
                                    register={register}
                                    isDisabled={isViewOnly}
                                    defaultValue={
                                        selectedAuditTypeItem.id === clickedItem?.audit_type_item?.id ?
                                            (clickedItem?.custom_color || clickedItem?.audit_type_item?.color || "") :
                                            (selectedAuditTypeItem.color || "")
                                    }
                                />
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={10} md={6}>
                            <>
                                {annotationType === AnnotationType.LINE && renderDimensionInfo(
                                    "length_feet",
                                    clickedItem.length_feet,
                                    "ft",
                                )}
                                {annotationType === AnnotationType.AREA && renderDimensionInfo(
                                    "area_feet",
                                    clickedItem.area_feet,
                                    "ft²",
                                )}
                                <GSPSTextArea
                                    value={clickedItem?.description}
                                    labelName={"Custom Description"}
                                    id={"description"}
                                    controlType={"input"}
                                    isRequired={false}
                                    register={register}
                                    rows={7}
                                    errors={errors}
                                    isDisabled={isViewOnly}
                                    setValue={setValue}
                                    tooltipMessage={"This description box is visible to anyone you share it " +
                                        "with—inside or outside your organization."}
                                />
                                {!isPublicAudit &&
                                    <div className="mt-2">
                                        <GSPSTextArea
                                            value={clickedItem?.notes}
                                            labelName={"Notes"}
                                            id={"notes"}
                                            controlType={"input"}
                                            isRequired={false}
                                            register={register}
                                            rows={7}
                                            errors={errors}
                                            isDisabled={isViewOnly}
                                            setValue={setValue}
                                            tooltipMessage={"This description box is visible only to people inside " +
                                                "your organization."}
                                        />
                                    </div>
                                }
                            </>
                        </Col>
                        {currentItem &&
                            annotationType &&
                            currentItem[`${annotationType}_images`] &&
                            clickedItem?.id === currentItem?.id && renderImagesAndVideos()}
                    </Row>
                </Container>
            </Form>
            <GSPSPopUpFooter style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <div style={{paddingLeft: "10px"}}>
                    {!isViewOnly && (<SVPModalDeleteButton
                        form={"issueForm"}
                        onClick={() => setCanDeleteModalShow(true)}
                        isDisabled={isSubmitting}
                    />)}
                </div>
                <div style={{display: "flex", gap: "8px"}}>
                    <GSPSCancelButton
                        onClick={handleClose}
                        isDisabled={isSubmitting}
                    />
                    {!isViewOnly && (
                        <GSPSSubmitButton
                            isDisabled={
                                (!isDirty && touched) ||
                                errors.zip?.message.length > 0 ||
                                isSubmitting
                            }
                            isLoading={isSubmitting}
                            controlSize={2}
                            offset={0}
                            buttonText={"Save"}
                            onClick={handleSubmit}
                            form={"issueForm"}
                        />
                    )}
                </div>
            </GSPSPopUpFooter>
            <ConfirmationModal
                show={canDeleteModalShow}
                onHide={() => setCanDeleteModalShow(false)}
                message={`Are you sure you want to delete this ${annotationType}?`}
                onConfirm={deleteAuditCall}
            />
        </GSPSPageWrapper>
    );
};

GSPSAnnotationModal.propTypes = {
    isModalOpen: PropTypes.bool.isRequired,
    onModalClose: PropTypes.func.isRequired,
    audit: PropTypes.object,
    clickedItem: PropTypes.object,
    annotationType: PropTypes.oneOf([AnnotationType.ISSUE, AnnotationType.AREA, AnnotationType.LINE]),
    issuesSeverities: PropTypes.array,
    deleteMedia: PropTypes.func,
    deleteAuditAnnotation: PropTypes.func,
    updateItem: PropTypes.func,
    typeItems: PropTypes.array,
    replaceTypeItemFilter: PropTypes.func,
    isPublicAudit: PropTypes.bool,
    isViewOnly: PropTypes.bool,
};

export default GSPSAnnotationModal;
